/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { WarningOutlined } from '@ant-design/icons'
import { Popover, Tag } from 'antd'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ModelAvailableBalance } from 'account-client'
import dayjs from 'dayjs'
import { AvailableBalanceState } from '@/pages/billing/interface'
import { officialSite } from '@/process.env'
import { timeFormatter } from '@/utils/timeFormatter'

export function AvailableState(
  props: React.PropsWithoutRef<{
    availableBalance: ModelAvailableBalance
  }>,
) {
  const { availableBalance } = props
  const { summary, details } = availableBalance.state
  const { t } = useTranslation()

  const { color, title, content, message } = useMemo(() => {
    const calculateDifferenceTime = (periodEnd: number) => {
      const { remainingDays, remainingHours, remainingMinutes, remainingSeconds } = timeFormatter.countdown(periodEnd)
      if (remainingDays > 0) {
        return (
          <Trans i18nKey="days_trans">
            （in <strong>{{ value: remainingDays }}</strong> days）
          </Trans>
        )
      }
      if (remainingHours > 0) {
        return (
          <Trans i18nKey="hours_trans">
            （in <strong>{{ value: remainingHours }}</strong> hours）
          </Trans>
        )
      }
      if (remainingMinutes > 0) {
        return (
          <Trans i18nKey="minutes_trans">
            （in <strong>{{ value: remainingMinutes }}</strong> minutes）
          </Trans>
        )
      }
      if (remainingSeconds > 0) {
        return (
          <Trans i18nKey="seconds_trans">
            （in <strong>{{ value: remainingSeconds }}</strong> seconds
          </Trans>
        )
      }
      return ''
    }
    switch (summary) {
      case AvailableBalanceState.overdraft:
        return {
          color: 'warning',
          message: t('available_state_overdraft_message'),
        }
      case AvailableBalanceState.extensionPeriod:
        return {
          color: 'error',
          message: (
            <>
              <Trans i18nKey="available_state_extension_period_message">
                service will suspend on <strong>{{ value: timeFormatter.format(details.extensionPeriodEnd, 'date') }}</strong>
              </Trans>
              {calculateDifferenceTime(details.extensionPeriodEnd)}
              {t('Please recharge as soon as possible!')}
            </>
          ),
          title: t('available_state_extension_period_title'),
          content: (
            <ul>
              <li>{t('available_state_extension_period_content_1')}</li>
              <li>{t('available_state_extension_period_content_2')}</li>
              <li>
                <Trans i18nKey="refer_documentation_desc">
                  Please refer to the
                  <a href={`${officialSite}/docs`} target="_blank">
                    documentation
                  </a>
                  for more information
                </Trans>
              </li>
            </ul>
          ),
        }
      case AvailableBalanceState.suspensionPeriod:
        return {
          color: 'error',
          message: (
            <>
              <Trans i18nKey="available_state_suspension_period_message">
                data will be archived on <strong>{{ value: timeFormatter.format(details.extensionPeriodEnd, 'date') }}</strong>
              </Trans>
              {calculateDifferenceTime(details.suspensionPeriodEnd)}
              {t('Please recharge as soon as possible!')}
            </>
          ),
          title: t('available_state_suspension_period_title'),
          content: (
            <ul>
              <li>{t('available_state_suspension_period_content_1')}</li>
              <li>{t('available_state_suspension_period_content_2')}</li>
              <li>{t('available_state_suspension_period_content_3')}</li>
              <li>{t('available_state_suspension_period_content_4')}</li>
              <li>
                <Trans i18nKey="refer_documentation_desc">
                  Please refer to the
                  <a href={`${officialSite}/docs`} target="_blank">
                    documentation
                  </a>
                  for more information
                </Trans>
              </li>
            </ul>
          ),
        }
      case AvailableBalanceState.backupPeriod:
        return {
          color: 'error',
          message: (
            <>
              <Trans i18nKey="available_state_backup_period_message">
                All resources will be permanently deleted on <strong>{{ value: timeFormatter.format(details.extensionPeriodEnd, 'date') }}</strong>
              </Trans>
              {calculateDifferenceTime(details.backupPeriodEnd)}
              {t('Please recharge as soon as possible!')}
            </>
          ),
          title: t('available_state_backup_period_title'),
          content: (
            <ul>
              <li>{t('available_state_backup_period_content_1')}</li>
              <li>{t('available_state_backup_period_content_2')}</li>
              <li>{t('available_state_backup_period_content_3')}</li>
              <li>{t('available_state_backup_period_content_4')}</li>
              <li>
                <Trans i18nKey="refer_documentation_desc">
                  Please refer to the
                  <a href={`${officialSite}/docs`} target="_blank">
                    documentation
                  </a>
                  for more information
                </Trans>
              </li>
            </ul>
          ),
        }
      case AvailableBalanceState.destructionPeriod:
        return {
          color: 'error',
          message: t('available_state_destruction_period_message'),
          title: t('available_state_destruction_period_title'),
          content: (
            <ul>
              <li>{t('available_state_destruction_period_content_1')}</li>
              <li>
                <Trans i18nKey="refer_documentation_desc">
                  Please refer to the
                  <a href={`${officialSite}/docs`} target="_blank">
                    documentation
                  </a>
                  for more information
                </Trans>
              </li>
            </ul>
          ),
        }
    }
  }, [availableBalance])

  return (
    <div>
      <Popover title={title} content={content}>
        <Tag icon={<WarningOutlined />} bordered={false} color={color}>
          {message}
        </Tag>
      </Popover>
    </div>
  )
}
